/**
 * useSessionRecorder
 *
 * Manages browser-side audio recording and real-time speech-to-text transcription
 * using the Web Speech API (SpeechRecognition). Each final transcript result is
 * forwarded to the caller via onFinalSegment for persistence.
 *
 * Browser support:
 * - SpeechRecognition: Chrome/Edge (full), Safari 14.1+ (partial), Firefox (none)
 * - MediaRecorder: all modern browsers
 *
 * The hook gracefully degrades when SpeechRecognition is unavailable — callers
 * receive isTranscriptionSupported=false and can show a fallback notice.
 */

import { useRef, useState, useCallback, useEffect } from 'react';

export type RecorderState = 'idle' | 'recording' | 'paused' | 'stopped';

export interface FinalSegment {
  text: string;
  startMs: number;
  endMs: number;
  confidence: number;
}

interface UseSessionRecorderOptions {
  /** Called for each final (stable) transcript chunk. */
  onFinalSegment: (segment: FinalSegment) => void;
  /** Called whenever the interim (live, may change) transcript updates. */
  onInterimText?: (text: string) => void;
}

interface UseSessionRecorderReturn {
  recorderState: RecorderState;
  interimText: string;
  isTranscriptionSupported: boolean;
  elapsedMs: number;
  start(): Promise<void>;
  pause(): void;
  resume(): void;
  stop(): number; // returns total elapsed ms
  requestPermission(): Promise<boolean>;
}

// Extend Window type for browser SpeechRecognition variants
declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition;
    webkitSpeechRecognition?: new () => SpeechRecognition;
  }
}

function getSpeechRecognitionClass(): (new () => SpeechRecognition) | null {
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

export function useSessionRecorder({
  onFinalSegment,
  onInterimText,
}: UseSessionRecorderOptions): UseSessionRecorderReturn {
  const [recorderState, setRecorderState] = useState<RecorderState>('idle');
  const [interimText, setInterimText] = useState('');
  const [elapsedMs, setElapsedMs] = useState(0);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedElapsedRef = useRef<number>(0);
  const segmentStartMsRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isTranscriptionSupported = getSpeechRecognitionClass() !== null;

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(() => {
      const sinceStart = Date.now() - startTimeRef.current;
      setElapsedMs(pausedElapsedRef.current + sinceStart);
    }, 250);
  }, [stopTimer]);

  const getElapsed = useCallback(() => {
    if (startTimeRef.current === 0) return pausedElapsedRef.current;
    return pausedElapsedRef.current + (Date.now() - startTimeRef.current);
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Keep stream alive for recording; stop it immediately if only checking
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch {
      return false;
    }
  }, []);

  const buildRecognition = useCallback((): SpeechRecognition | null => {
    const SpeechClass = getSpeechRecognitionClass();
    if (!SpeechClass) return null;

    const rec = new SpeechClass();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || 'en-US';

    rec.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          const text = result[0].transcript.trim();
          if (text) {
            const now = getElapsed();
            onFinalSegment({
              text,
              startMs: segmentStartMsRef.current,
              endMs: now,
              confidence: result[0].confidence ?? 1,
            });
            segmentStartMsRef.current = now;
          }
        } else {
          interim += result[0].transcript;
        }
      }
      setInterimText(interim);
      onInterimText?.(interim);
    };

    rec.onerror = (event: SpeechRecognitionErrorEvent) => {
      // 'no-speech' is benign — recognition restarts automatically
      if (event.error === 'no-speech') return;
      console.warn('[useSessionRecorder] SpeechRecognition error:', event.error);
    };

    // Restart automatically to keep transcribing beyond the browser's max duration
    rec.onend = () => {
      if (recorderState === 'recording') {
        try {
          rec.start();
        } catch {
          // Already started — ignore
        }
      }
    };

    return rec;
  }, [onFinalSegment, onInterimText, getElapsed, recorderState]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
    } catch {
      return;
    }

    const rec = buildRecognition();
    recognitionRef.current = rec;

    startTimeRef.current = Date.now();
    pausedElapsedRef.current = 0;
    segmentStartMsRef.current = 0;
    setElapsedMs(0);

    if (rec) {
      try {
        rec.start();
      } catch {
        // Already running — ignore
      }
    }

    startTimer();
    setRecorderState('recording');
    setInterimText('');
  }, [buildRecognition, startTimer]);

  const pause = useCallback(() => {
    if (recorderState !== 'recording') return;

    pausedElapsedRef.current += Date.now() - startTimeRef.current;
    startTimeRef.current = 0;
    stopTimer();

    recognitionRef.current?.stop();
    setRecorderState('paused');
    setInterimText('');
  }, [recorderState, stopTimer]);

  const resume = useCallback(() => {
    if (recorderState !== 'paused') return;

    startTimeRef.current = Date.now();
    segmentStartMsRef.current = pausedElapsedRef.current;
    startTimer();

    const rec = buildRecognition();
    recognitionRef.current = rec;
    try {
      rec?.start();
    } catch {
      // Ignore
    }

    setRecorderState('recording');
  }, [recorderState, startTimer, buildRecognition]);

  const stop = useCallback((): number => {
    const total = getElapsed();

    stopTimer();
    recognitionRef.current?.stop();
    recognitionRef.current = null;

    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;

    startTimeRef.current = 0;
    pausedElapsedRef.current = 0;
    segmentStartMsRef.current = 0;
    setElapsedMs(total);
    setRecorderState('stopped');
    setInterimText('');

    return total;
  }, [getElapsed, stopTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();
      recognitionRef.current?.stop();
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [stopTimer]);

  return {
    recorderState,
    interimText,
    isTranscriptionSupported,
    elapsedMs,
    start,
    pause,
    resume,
    stop,
    requestPermission,
  };
}
